import { OHLC_BAR_WIDTH_RATIO } from "../constants";
import { centredOffset, buildRectShapes } from "./rectShapes";
import { colorSlotOf } from "./seriesPalette";

const SHAPE_STEM = 0;
const SHAPE_OPEN_TICK = 1;
const SHAPE_CLOSE_TICK = 2;
export const RECTS_PER_BAR = 3;
// The bar is three strokes wide: open tick, stem, close tick
const STROKES_PER_BAR = 3;

/**
 * Rectangle instances for OHLC bars (05-bars.jpg): a stem (high–low), the
 * open tick on the left and the close tick on the right. Prices are
 * relative to `priceBase` (see mainSeries).
 *
 * @param {object[]} bars - { open, high, low, close }
 * @param {number} priceBase
 * @param {number} floatsPerRect - rectRenderer.floatsPerInstance
 * @param {number} [firstIndex] - bar index of bars[0], when only the tail is rebuilt
 * @returns {Float32Array}
 */
export const buildBarInstances = (bars, priceBase, floatsPerRect, firstIndex = 0) => {
  const data = new Float32Array(bars.length * RECTS_PER_BAR * floatsPerRect);
  let offset = 0;

  bars.forEach((bar, position) => {
    const index = firstIndex + position;
    const colorSlot = colorSlotOf(bar);
    const open = bar.open - priceBase;
    const close = bar.close - priceBase;

    data.set(
      [
        index, bar.high - priceBase, bar.low - priceBase, SHAPE_STEM, colorSlot,
        index, open, open, SHAPE_OPEN_TICK, colorSlot,
        index, close, close, SHAPE_CLOSE_TICK, colorSlot,
      ],
      offset,
    );
    offset += RECTS_PER_BAR * floatsPerRect;
  });

  return data;
};

/**
 * Stem and tick shapes in whole device pixels, as in PO: the bar takes
 * OHLC_BAR_WIDTH_RATIO of the bar spacing and is three strokes wide, so the
 * ticks are as thick as the stem and stick out from it by the same length.
 * The stroke is at least 1 device px and never so wide that neighbouring
 * bars overlap. Shadow style does not apply to bars.
 *
 * @param {number} barSpacing - CSS px
 * @param {number} pixelRatio
 * @returns {{ widths: Float32Array, offsets: Float32Array, minHeight: number }}
 */
export const barShapes = (barSpacing, pixelRatio) => {
  const spacing = barSpacing * pixelRatio;
  const stroke = Math.max(
    1,
    Math.min(
      Math.round((spacing * OHLC_BAR_WIDTH_RATIO) / STROKES_PER_BAR),
      Math.floor(spacing / STROKES_PER_BAR),
    ),
  );
  const stemOffset = centredOffset(stroke);

  return buildRectShapes(
    [
      [stroke, stemOffset],
      [stroke, stemOffset - stroke],
      [stroke, stemOffset + stroke],
    ],
    stroke,
  );
};
